import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Shared new-message signals for DMs: unread total (nav badge + tab title) and a short
// chime + document-title flash when the unread count grows. Polls every 15 s; no assets.
let audioCtx = null;
export function playDmChime() {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const t = audioCtx.currentTime;
    [[880, 0], [1175, 0.09]].forEach(([f, d]) => {
      const o = audioCtx.createOscillator(); const g = audioCtx.createGain();
      o.type = "sine"; o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, t + d); g.gain.exponentialRampToValueAtTime(0.12, t + d + 0.01); g.gain.exponentialRampToValueAtTime(0.0001, t + d + 0.18);
      o.connect(g).connect(audioCtx.destination); o.start(t + d); o.stop(t + d + 0.2);
    });
  } catch { /* autoplay blocked or no audio */ }
}

// Single owner of document.title: base title + unread prefix + transient flash.
const titleState = { base: null, unread: 0, flashing: false, timer: null, until: 0 };
function applyTitle() {
  if (titleState.base == null) titleState.base = document.title.replace(/^\(\d+\) |^💬 /, "");
  const normal = titleState.unread > 0 ? `(${titleState.unread}) ${titleState.base}` : titleState.base;
  document.title = titleState.flashing ? "💬 New message" : normal;
}
export function setUnreadTitle(n) { titleState.unread = n; applyTitle(); }
export function flashTitle() {
  if (titleState.base == null) titleState.base = document.title.replace(/^\(\d+\) |^💬 /, "");
  titleState.until = Date.now() + 9000;
  if (titleState.timer) return;
  titleState.flashing = true; applyTitle();
  titleState.timer = setInterval(() => {
    if (Date.now() > titleState.until) { clearInterval(titleState.timer); titleState.timer = null; titleState.flashing = false; applyTitle(); return; }
    titleState.flashing = !titleState.flashing; applyTitle();
  }, 900);
}

export default function useUnreadDMs({ intervalMs = 15000 } = {}) {
  const { user } = useAuth();
  const [unread, setUnread] = useState({ total: 0, conversations: 0, latest_at: null });
  const prev = useRef(null);
  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/conversations/unread-count");
      setUnread(data);
      if (prev.current && data.latest_at && (!prev.current.latest_at || data.latest_at > prev.current.latest_at) && data.total > 0) {
        playDmChime();
        flashTitle();
      }
      prev.current = data;
    } catch { /* ignore */ }
  }, [user]);
  useEffect(() => {
    load();
    const t = setInterval(load, intervalMs);
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, [load, intervalMs]);
  useEffect(() => { setUnreadTitle(unread.total); }, [unread.total]);
  return { ...unread, refresh: load };
}
